import { toCurrency } from '../../utils/CurrencyUtil'

const EMPTY_STRINGS = new Set(['', 'n/a', 'na', 'null', 'undefined'])

const FIELD_ALIASES = {
  dob: ['dob'],
  gender: ['usergender', 'userGender', 'gender'],
  salary: ['salary'],
  goals: ['goals'],
  fears: ['fears'],
  members: ['members'],
  investment: ['investment'],
  insurance: ['insuranceData', 'insurancedata'],
  lumpSum: ['lumpSum', 'lumpsum'],
  monthlySaving: ['monthlySaving', 'monthlysaving'],
}

const INSURANCE_LABELS = {
  'Private Medical Insurance': 'Health Insurance',
  'Corporate Medical Insurance': 'Corporate Medical Insurance',
  'Term Insurance': 'Term Insurance',
}

const KNOWN_KEYS = new Set(Object.values(FIELD_ALIASES).flat())

export const answersFromResponse = (userRes) => {
  if (!Array.isArray(userRes)) return {}
  return userRes.reduce((acc, item) => {
    if (!item || typeof item !== 'object') return acc
    const key = Object.keys(item)[0]
    if (key) acc[key] = item[key]
    return acc
  }, {})
}

const pick = (answers, aliases) => {
  for (const key of aliases) {
    if (Object.prototype.hasOwnProperty.call(answers, key)) return answers[key]
  }
  return undefined
}

export const isBlank = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === 'boolean') return false
  if (typeof value === 'number') return !Number.isFinite(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return !trimmed || EMPTY_STRINGS.has(trimmed.toLowerCase())
  }
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') {
    return Object.values(value).every((item) => item === false || isBlank(item))
  }
  return false
}

export const displayText = (value) => {
  if (value === null || value === undefined) return 'Not provided'
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || EMPTY_STRINGS.has(trimmed.toLowerCase())) return 'Not provided'
    if (trimmed.toLowerCase() === 'none') return 'None reported'
    return trimmed
  }
  if (typeof value === 'number') return String(value)
  return 'Not provided'
}

export const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const parsed = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

export const formatRupee = (value) => {
  const amount = parseAmount(value)
  if (amount === null) return null
  return `₹${toCurrency(amount)}`
}

export const formatDisplayDate = (value) => {
  if (isBlank(value)) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return displayText(value)
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const getAgeFromDob = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - date.getFullYear()
  const monthDiff = today.getMonth() - date.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1
  if (age < 0 || age > 120) return null
  return age
}

const asList = (value) => {
  if (Array.isArray(value)) return value.filter((item) => !isBlank(item))
  if (isBlank(value)) return []
  return [value]
}

const parseMembers = (value) => {
  if (isBlank(value)) return { count: null, details: [] }

  if (typeof value === 'number' || (typeof value === 'string' && parseAmount(value) !== null)) {
    return { count: parseAmount(value), details: [] }
  }

  if (Array.isArray(value)) {
    const details = value
      .map((member) => {
        if (typeof member === 'string') return { name: member }
        if (member && typeof member === 'object') return member
        return null
      })
      .filter(Boolean)
    return { count: details.length, details }
  }

  return { count: null, details: [] }
}

const parseLumpSum = (value) => {
  if (isBlank(value)) return { hasLumpSum: null, amount: null }
  if (typeof value === 'object') {
    const answer = displayText(value.ans)
    const hasLumpSum = value.ans ? String(value.ans).toLowerCase() === 'yes' : null
    return {
      hasLumpSum,
      answer: value.ans ? answer : null,
      amount: parseAmount(value.amount),
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const amount = parseAmount(value)
    return { hasLumpSum: amount !== null ? amount > 0 : null, amount }
  }
  return { hasLumpSum: null, amount: null }
}

const parseInvestment = (value) => {
  if (isBlank(value)) return { types: [], other: null, willingToShare: null, noneReported: false }
  if (Array.isArray(value)) {
    const types = value.filter((item) => item && item !== 'Not yet')
    return {
      types,
      other: null,
      willingToShare: null,
      noneReported: value.includes('Not yet') && types.length === 0,
    }
  }
  if (typeof value === 'object') {
    const types = asList(value.type).filter((item) => item !== 'Not yet')
    const noneReported =
      asList(value.type).includes('Not yet') && types.length === 0 && isBlank(value.other)
    return {
      types,
      other: isBlank(value.other) ? null : displayText(value.other),
      willingToShare: Boolean(value.isChecked),
      noneReported,
    }
  }
  return { types: [String(value)], other: null, willingToShare: null, noneReported: false }
}

const parseInsurance = (value) => {
  const items = asList(value)
  const noneReported = items.includes('Not yet') && items.every((item) => item === 'Not yet')
  return {
    noneReported,
    items: items.filter((item) => item !== 'Not yet').map((item) => INSURANCE_LABELS[item] || item),
  }
}

const humanizeKey = (key) =>
  String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase())

export const buildInvestorProfile = ({ userRes, selectedUser, singleUser }) => {
  const answers = answersFromResponse(userRes)
  const salary = parseAmount(pick(answers, FIELD_ALIASES.salary))
  const monthlySaving = parseAmount(pick(answers, FIELD_ALIASES.monthlySaving))
  const goals = asList(pick(answers, FIELD_ALIASES.goals))
  const fears = asList(pick(answers, FIELD_ALIASES.fears))
  const investment = parseInvestment(pick(answers, FIELD_ALIASES.investment))
  const insurance = parseInsurance(pick(answers, FIELD_ALIASES.insurance))
  const lumpSum = parseLumpSum(pick(answers, FIELD_ALIASES.lumpSum))
  const members = parseMembers(pick(answers, FIELD_ALIASES.members))
  const dob = pick(answers, FIELD_ALIASES.dob)
  const gender = pick(answers, FIELD_ALIASES.gender)
  const age = getAgeFromDob(dob)

  const fullName =
    `${selectedUser?.firstName || singleUser?.firstName || ''} ${
      selectedUser?.lastName || singleUser?.lastName || ''
    }`.trim() || 'Investor'

  const activeGoals = goals.filter((goal) => goal !== 'No goals')
  const primaryGoal = activeGoals[0] || (goals.includes('No goals') ? 'No goals' : null)
  const annualIncome = salary !== null ? salary * 12 : null
  const savingsRate =
    salary && salary > 0 && monthlySaving !== null ? (monthlySaving / salary) * 100 : null

  const additionalDetails = Object.entries(answers)
    .filter(([key]) => !KNOWN_KEYS.has(key))
    .map(([key, value]) => ({
      label: humanizeKey(key),
      value,
    }))

  return {
    fullName,
    phoneNumber: selectedUser?.phoneNumber || singleUser?.phoneNumber || null,
    isCompleted: Boolean(selectedUser?.isCompleted ?? singleUser?.isCompleted),
    submittedOn: selectedUser?.createdTimestamp || singleUser?.createdTimestamp,
    updatedOn: selectedUser?.updatedTimestamp || singleUser?.updatedTimestamp,
    salary,
    monthlySaving,
    annualIncome,
    savingsRate,
    primaryGoal,
    goals: activeGoals,
    hasNoGoals: goals.includes('No goals') && activeGoals.length === 0,
    fears,
    investment,
    insurance,
    lumpSum,
    members,
    dob,
    age,
    gender,
    additionalDetails,
  }
}

export const buildAdvisorSummary = (profile) => {
  const sentences = []

  if (profile.salary !== null && profile.monthlySaving !== null) {
    const rate =
      profile.savingsRate !== null ? ` (${Math.round(profile.savingsRate)}% of take-home pay)` : ''
    sentences.push(
      `Take-home income is ${formatRupee(
        profile.salary,
      )} per month, with monthly savings of ${formatRupee(profile.monthlySaving)}${rate}.`,
    )
  } else if (profile.salary !== null) {
    sentences.push(`Take-home income is ${formatRupee(profile.salary)} per month.`)
  } else if (profile.monthlySaving !== null) {
    sentences.push(`Monthly savings are ${formatRupee(profile.monthlySaving)}.`)
  }

  if (profile.primaryGoal && profile.primaryGoal !== 'No goals') {
    const extra =
      profile.goals.length > 1 ? ` Additional goals: ${profile.goals.slice(1).join(', ')}.` : ''
    sentences.push(`Primary financial goal is ${profile.primaryGoal}.${extra}`)
  } else if (profile.hasNoGoals) {
    sentences.push('No financial goals were reported.')
  }

  const holdings = [...profile.investment.types]
  if (profile.investment.other) holdings.push(profile.investment.other)
  if (profile.investment.noneReported) {
    sentences.push('No existing investments were reported.')
  } else if (holdings.length > 0) {
    sentences.push(`Existing investments: ${holdings.join(', ')}.`)
  }

  if (profile.insurance.noneReported) {
    sentences.push('No insurance coverage was reported.')
  } else if (profile.insurance.items.length > 0) {
    sentences.push(`Insurance coverage: ${profile.insurance.items.join(', ')}.`)
  }

  if (profile.members.count !== null) {
    sentences.push(
      profile.members.count === 1
        ? '1 dependent was reported.'
        : `${profile.members.count} dependents were reported.`,
    )
  }

  if (profile.lumpSum.hasLumpSum === true) {
    sentences.push(
      profile.lumpSum.amount !== null
        ? `A lump-sum amount of ${formatRupee(profile.lumpSum.amount)} is available to invest.`
        : 'A lump-sum amount is available to invest.',
    )
  } else if (profile.lumpSum.hasLumpSum === false) {
    sentences.push('No lump-sum amount is available to invest now.')
  }

  const concerns = profile.fears.filter((fear) => fear.toLowerCase() !== 'none')
  if (concerns.length > 0) {
    sentences.push(`Financial concerns: ${concerns.join(', ')}.`)
  } else if (profile.fears.some((fear) => fear.toLowerCase() === 'none')) {
    sentences.push('No financial concerns were reported.')
  }

  return sentences
}

const reportValue = (value) => value || 'Not provided'

export const buildProfileReport = (profile) => {
  const summary = buildAdvisorSummary(profile)
  const lines = [
    `${profile.fullName}`,
    'Financial Profile',
    `Status: ${profile.isCompleted ? 'Completed' : 'Pending'}`,
    `Submitted: ${formatDisplayDate(profile.submittedOn) || 'Not provided'}`,
    `Last updated: ${formatDisplayDate(profile.updatedOn) || 'Not provided'}`,
    '',
    'Advisor summary',
    ...(summary.length ? summary : ['Not enough questionnaire data to summarise.']),
    '',
    'Investor snapshot',
    `Monthly income: ${reportValue(formatRupee(profile.salary))}`,
    `Monthly savings: ${reportValue(formatRupee(profile.monthlySaving))}`,
    `Estimated annual income: ${reportValue(formatRupee(profile.annualIncome))}`,
    `Savings rate: ${
      profile.savingsRate !== null ? `${Math.round(profile.savingsRate)}%` : 'Not provided'
    }`,
    `Primary financial goal: ${reportValue(
      profile.primaryGoal === 'No goals' ? 'None reported' : profile.primaryGoal,
    )}`,
    `Dependents: ${
      profile.members.count !== null ? String(profile.members.count) : 'Not provided'
    }`,
    `Existing investments: ${
      profile.investment.noneReported
        ? 'None reported'
        : [...profile.investment.types, profile.investment.other].filter(Boolean).join(', ') ||
          'Not provided'
    }`,
    '',
    'Financial goals',
    profile.goals.length ? profile.goals.map((goal) => `- ${goal}`).join('\n') : 'None reported',
    '',
    'Insurance coverage',
    profile.insurance.noneReported
      ? 'None reported'
      : profile.insurance.items.length
      ? profile.insurance.items.map((item) => `- ${item}`).join('\n')
      : 'Not provided',
    '',
    'Personal information',
    `Date of birth: ${reportValue(formatDisplayDate(profile.dob))}`,
    `Age: ${profile.age !== null ? String(profile.age) : 'Not provided'}`,
    `Gender: ${displayText(profile.gender)}`,
  ]

  return lines.join('\n')
}
